"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@universe/ui/components/alert-dialog";
import { useTranslations } from "next-intl";
import { useMemo, useRef } from "react";

import { getCountdownAnnouncement } from "./getCountdownAnnouncement";

// Countdown ring geometry. The number sits centered inside the ring in a
// fixed-size box (tabular-nums) so it never shifts as the digits change
// (60 → 9 → …).
const RING_SIZE = 112;
const RING_STROKE = 6;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export interface StaySignedInDialogProps {
  /** Whether the warning is showing (driven by the controller's `isPrompted`). */
  open: boolean;
  /** Whole seconds left before the hard logout. */
  remainingSeconds: number;
  /** Total warning-window length in whole seconds — the ring's denominator. */
  totalSeconds: number;
  /** "Stay signed in" — the safe, non-destructive default. */
  onStaySignedIn: () => void;
  /** "Sign out now" — immediate logout. */
  onSignOutNow: () => void;
}

/**
 * Pure presentational "Are you still here?" warning. No auth / library
 * knowledge — the controller wires it to `useIdleLogout`.
 *
 * Accessibility (design §9):
 * - Radix AlertDialog → `role="alertdialog"`, `aria-modal`, focus trap.
 * - Focus moves to the non-destructive "Stay signed in" on open; Radix
 *   returns focus to the prior element on close.
 * - Escape is mapped to "Stay signed in" (extend), not an ambiguous dismiss;
 *   outside-click dismissal is disabled.
 * - The countdown ring + ticking number are visible but `aria-hidden`; two
 *   always-mounted live regions (one `polite`, one `assertive`) announce at
 *   intervals — the announcement is written into whichever matches the current
 *   phase, never every second.
 * - The ring depletes via a CSS `stroke-dashoffset` transition gated behind
 *   `motion-safe:`, so it glides smoothly when motion is allowed and steps
 *   (no tween) under `prefers-reduced-motion: reduce`. No framer-motion /
 *   AnimatePresence (transitions.md) → no Radix-modal mount hazard.
 * - The countdown ring, the number inside it, and the primary button all use
 *   shades derived from the active whitelabel brand, so the dialog themes per
 *   whitelabel rather than using the fixed `--primary`/`--foreground` tokens:
 *   the ring is `brand-boundary` (3:1 mark), the number and the button fill
 *   under white text are `brand-strong` (7:1). The button darkens on hover
 *   rather than fading: `brand-strong` at 90% over white drops white text to
 *   6.6:1 for the lightest-capped brand, under the 7:1 it holds at rest.
 * - All text nodes are wrapped in `<span>` (translation-tolerance T-patterns);
 *   this dialog is conceptual copy, so it DOES translate (no `translate="no"`).
 */
export function StaySignedInDialog({
  open,
  remainingSeconds,
  totalSeconds,
  onStaySignedIn,
  onSignOutNow,
}: StaySignedInDialogProps) {
  const t = useTranslations("inactivity");
  const staySignedInRef = useRef<HTMLButtonElement>(null);

  const { announce, assertive } = useMemo(
    () => getCountdownAnnouncement(remainingSeconds),
    [remainingSeconds]
  );

  // Write the announcement into the region whose fixed politeness matches the
  // current phase; the other region stays empty.
  const announcement = announce
    ? t("srCountdown", { seconds: remainingSeconds })
    : "";
  const politeText = announce && !assertive ? announcement : "";
  const assertiveText = announce && assertive ? announcement : "";

  // Ring fraction: full at the start of the window, empty at zero. Clamped so a
  // stray out-of-range value can't produce a broken arc.
  const fraction =
    totalSeconds > 0
      ? Math.min(1, Math.max(0, remainingSeconds / totalSeconds))
      : 0;
  const dashOffset = RING_CIRCUMFERENCE * (1 - fraction);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        data-testid="inactivity-dialog"
        className="sm:max-w-sm"
        onEscapeKeyDown={(event) => {
          // Escape extends the session (safe default), it does not dismiss.
          // (Radix AlertDialog already ignores outside-click dismissal, so the
          // user must make an explicit choice or let the countdown run out.)
          event.preventDefault();
          onStaySignedIn();
        }}
        onOpenAutoFocus={(event) => {
          // Focus the non-destructive primary action rather than the cancel.
          event.preventDefault();
          staySignedInRef.current?.focus();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            <span>{t("title")}</span>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span>{t("description")}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Circular countdown. aria-hidden — the live regions below carry the
            announcement (throttled). The number is centered in a fixed-size box
            (tabular-nums) so it never shifts as the digits change. The ring's
            depletion is a CSS stroke-dashoffset transition (motion-safe). */}
        <div className="flex justify-center py-2" aria-hidden="true">
          <div
            className="relative"
            style={{ width: RING_SIZE, height: RING_SIZE }}
            data-testid="inactivity-countdown"
          >
            <svg
              className="-rotate-90"
              width={RING_SIZE}
              height={RING_SIZE}
              viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            >
              <circle
                className="stroke-brand-primary/15"
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                strokeWidth={RING_STROKE}
              />
              <circle
                className="stroke-brand-boundary motion-safe:transition-[stroke-dashoffset] motion-safe:duration-1000 motion-safe:ease-linear"
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-brand-strong text-4xl font-semibold tabular-nums">
                {remainingSeconds}
              </span>
            </div>
          </div>
        </div>

        {/* TWO always-mounted SR-only live regions with FIXED politeness.
            Some screen readers latch a region's politeness at first
            registration, so toggling aria-live on a single node is unreliable
            (design §9). The announcement is written into whichever region
            matches the current phase (polite for the periodic marks, assertive
            in the final stretch); the other stays empty. Both stay empty
            between marks so neither re-reads every second. */}
        <span
          className="sr-only"
          aria-live="polite"
          aria-atomic="true"
          data-testid="inactivity-sr-countdown-polite"
        >
          <span>{politeText}</span>
        </span>
        <span
          className="sr-only"
          aria-live="assertive"
          aria-atomic="true"
          data-testid="inactivity-sr-countdown-assertive"
        >
          <span>{assertiveText}</span>
        </span>

        {/* Stacked, full-width buttons — primary (brand) on top. */}
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <AlertDialogAction
            ref={staySignedInRef}
            data-testid="inactivity-stay-signed-in"
            className="w-full bg-brand-strong text-white hover:bg-[color-mix(in_srgb,var(--brand-strong),black_15%)]"
            onClick={(event) => {
              event.preventDefault();
              onStaySignedIn();
            }}
          >
            <span>{t("staySignedIn")}</span>
          </AlertDialogAction>
          <AlertDialogCancel
            data-testid="inactivity-sign-out-now"
            className="w-full"
            onClick={(event) => {
              event.preventDefault();
              onSignOutNow();
            }}
          >
            <span>{t("signOutNow")}</span>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
